import { ChangePasswordRequest } from "@9amhealth/openapi";
import { AutoForm, AutoFormInputField, Button } from "@9amhealth/shared";
import { Cubit } from "blac";
import type { CSSProperties, FC } from "react";
import React from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { CheckmarkIcon } from "src/constants/icons";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import Path from "src/constants/path";
import { PASSWORD_MIN_LENGTH } from "src/constants/schemaValidation";
import { createAppPath } from "src/lib/createAppPath";
import translate from "src/lib/translate";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import UserCubit from "src/state/UserCubit/UserCubit";
import { authenticationState, toast, tracker, useBloc } from "src/state/state";
import Loader from "src/ui/components/Loader/Loader";
import Track from "src/ui/components/Track/Track";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";
import {
  createPasswordSchema,
  EmailSchema
} from "@9amhealth/shared/src/lib/zod";
import FormError from "../FormError/FormError";
import { InputFocusVirtualKeyboardPadding } from "../StyledComponents/InputFocusVirtualKeyboardPadding";
import { extractErrorCode } from "src/lib/errors";

interface ChangePasswordState {
  errorCode?: string;
  isLoading: boolean;
}

interface ChangePasswordCallbacks {
  onSuccess?: () => void;
  onError?: (errorCode: string) => void;
}

export class ChangePasswordBloc extends Cubit<ChangePasswordState> {
  navigate: NavigateFunction;
  callbacks: ChangePasswordCallbacks;

  constructor(
    navigate: NavigateFunction,
    callbacks: ChangePasswordCallbacks = {}
  ) {
    super({ isLoading: false });
    this.navigate = navigate;
    this.callbacks = callbacks;
  }

  changeErrorCode = (code: string) => {
    this.emit({ ...this.state, errorCode: code });
  };

  handleSubmit = async (data: {
    newPassword: string;
    oldPassword?: string;
  }): Promise<void> => {
    this.emit({ ...this.state, isLoading: true, errorCode: undefined });

    let error;
    try {
      await authenticationState.changePassword({
        newPassword: data.newPassword,
        currentPassword: data.oldPassword
      } as ChangePasswordRequest);
    } catch (e) {
      error = e;
    }

    this.emit({ ...this.state, isLoading: false });

    if (error) {
      const errorCode = extractErrorCode(error);
      this.emit({ ...this.state, errorCode });
      this.callbacks.onError?.(errorCode);
    } else {
      tracker.track(TrackEvent.changePassword, {
        type: TrackType.complete
      });
      toast.success("password_change_success");
      this.callbacks.onSuccess?.();
      this.navigate(createAppPath(Path.profile), { replace: true });
    }
  };
}

const Schema = z.object({
  email: EmailSchema,
  oldPassword: z.string({
    error: translate("error_validation_required")
  }),
  newPassword: createPasswordSchema({
    minLength: PASSWORD_MIN_LENGTH,
    requiredMessage: translate("error_validation_required"),
    tooShortMessage: translate("invalid_password")
  })
});

interface ChangePasswordProps {
  onSuccess?: () => void;
  onError?: (errorCode: string) => void;
}

const ChangePassword: FC<ChangePasswordProps> = ({ onSuccess, onError }) => {
  const [showCheckmarkIcon, setShowCheckmarkIcon] = React.useState(false);
  const navigate = useNavigate();

  const [state, { handleSubmit, changeErrorCode }] = useBloc(
    ChangePasswordBloc,
    {
      create: () => new ChangePasswordBloc(navigate, { onSuccess, onError })
    }
  );

  const [{ userData }] = useBloc(UserCubit, {
    subscribe: false
  });

  function handleInputChange(value: string) {
    if (value.length >= PASSWORD_MIN_LENGTH) {
      setShowCheckmarkIcon(true);
    } else {
      setShowCheckmarkIcon(false);
    }
  }

  return (
    <InputFocusVirtualKeyboardPadding
      style={
        {
          "--section-max-width": `${APP_CONTENT_WIDTH_WITHOUT_PADDING}px`
        } as CSSProperties
      }
    >
      <Loader fixed background active={state.isLoading} />
      <Track event={TrackEvent.changePassword} type={TrackType.start} />

      <nine-spacer s="xl"></nine-spacer>
      <nine-heading>
        <h3 className="as-h4-large">
          <Translate msg="setNewPassword" />
        </h3>
      </nine-heading>

      <nine-spacer s="xl"></nine-spacer>

      <AutoForm
        schema={Schema}
        onChange={() => changeErrorCode("")}
        onSubmit={(data) => void handleSubmit(data)}
        initialValue={{ email: userData?.email }}
      >
        <nine-spacer s="sm"></nine-spacer>
        <AutoFormInputField
          name="email"
          label={translate("emailAddress")}
          type="email"
          isDisabled={true}
          autoComplete="username"
          data-testid="email"
          style={{ display: "none" }}
        />
        <AutoFormInputField
          isPasswordToggleEnabled
          name="oldPassword"
          label={translate("password.old")}
          type="password"
          isRequired
          data-testid="old-Password"
        />

        <nine-spacer s="md"></nine-spacer>
        <AutoFormInputField
          name="newPassword"
          label={translate("password.new")}
          type="password"
          isPasswordToggleEnabled
          onChange={(value: string) => handleInputChange(value)}
          isRequired
          description={`${!showCheckmarkIcon ? "• " : ""} ${translate("invalid_password")}`}
          DescriptionIcon={showCheckmarkIcon ? CheckmarkIcon : undefined}
        />
        <nine-spacer></nine-spacer>

        <FormError
          code={state.errorCode}
          msg="error_password_reset_code"
          data-testid="form-error"
        />
        <Button type="submit" theme="sunrise" style={{ margin: "auto" }}>
          <Translate msg={"confirm"} />
        </Button>
      </AutoForm>
    </InputFocusVirtualKeyboardPadding>
  );
};

export default ChangePassword;
